import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ImagePlus, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api, publicUploadImage, fileUrl } from '../../lib/api';
import { SectionHeader } from '../../components/Shared';

const RACES = [
  { value: 'mayor', label: 'Mayor' },
  { value: 'council', label: 'Ward Councillor' },
  { value: 'trustee', label: 'School Board Trustee' },
  { value: 'general', label: 'General (all of Kawartha Lakes)' },
];
const WARDS = ['1', '2', '3', '4', '5', '6', '7', '8'];

const EMPTY = {
  title: '', description: '', organizer: '', submitter_name: '', submitter_email: '',
  date: '', start_time: '', end_time: '', venue_name: '', address: '', online_url: '',
  races: [], wards: [], candidates: '', event_type: 'meet_greet', image_url: '', source_url: '', registration_url: '',
  consent: false, website: '',
};

// Public form. Everything submitted waits for approval in Admin. Submitter
// name and email are stored privately and never shown on the site.
export default function SubmitEvent() {
  const [form, setForm] = useState(EMPTY);
  const [types, setTypes] = useState({});
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);
  const [errors, setErrors] = useState({});
  const startedAt = useRef(Date.now() / 1000);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/election/events', { params: { when: 'upcoming', limit: 1 } }).then((r) => setTypes(r.data?.event_types || {})).catch(() => {});
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: '' })); };
  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);

  const validate = () => {
    const e = {};
    if (form.title.trim().length < 4) e.title = 'Give the event a clear title (at least 4 characters).';
    if (form.description.trim().length < 10) e.description = 'Add a short description (at least 10 characters).';
    if (form.organizer.trim().length < 2) e.organizer = 'Who is organizing this event?';
    if (!form.date) e.date = 'Choose the event date.';
    if (form.races.length === 0) e.races = 'Choose at least one race.';
    if (form.races.includes('council') && form.wards.length === 0) e.wards = 'Choose the ward(s) for a Councillor event.';
    if (!(form.venue_name.trim() || form.address.trim() || form.online_url.trim())) e.venue_name = 'Add a venue or address, or an online event link.';
    if (form.submitter_name.trim().length < 2) e.submitter_name = 'Your name helps us confirm details if needed.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.submitter_email.trim())) e.submitter_email = 'Enter a valid email address.';
    if (!form.consent) e.consent = 'Please confirm the details may be published.';
    for (const k of ['online_url', 'source_url', 'registration_url']) {
      if (form[k].trim() && !/^https?:\/\//i.test(form[k].trim())) e[k] = 'Links must start with http:// or https://';
    }
    return e;
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await publicUploadImage(file);
      set('image_url', r.url);
      toast.success('Poster uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed. Use a jpg, png, gif or webp under 8MB.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) { toast.error('Please fix the highlighted fields.'); return; }
    setSending(true);
    try {
      const body = { ...form, ward: form.wards.length === 1 ? form.wards[0] : 'citywide', form_started_at: startedAt.current };
      const r = await api.post('/election/events/submit', body);
      setDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'The event could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="submit-event-success">
        <SectionHeader kicker="RECEIVED" title="Thank you. Your event is in the review queue." />
        <Card className="border-emerald-200 bg-emerald-50/50"><CardContent className="p-6">
          <p className="flex items-start gap-2 text-[#1a2744] font-semibold"><CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" /> {done.message}</p>
          <p className="text-sm text-slate-600 mt-3">Reference <span className="font-mono font-bold text-[#1a2744]" data-testid="submit-event-reference">{done.reference}</span>. Nothing is published until it has been checked.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="submit-event-view-list"><Link to="/elections/events">View the Election Event List</Link></Button>
            <Button variant="outline" onClick={() => { setForm(EMPTY); setDone(null); startedAt.current = Date.now() / 1000; }} className="h-11 border-[#1a2744] text-[#1a2744] font-bold" data-testid="submit-event-another">Submit another event</Button>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  const Err = ({ k }) => (errors[k] ? <p className="text-xs text-red-700 mt-1" data-testid={`submit-error-${k}`}>{errors[k]}</p> : null);
  const inputCls = (k) => `h-11 bg-white ${errors[k] ? 'border-red-400 focus-visible:ring-red-300' : ''}`;

  return (
    <div data-testid="submit-event-page">
      <SectionHeader kicker="HELP RESIDENTS FIND IT" title="Submit an election event" dek="Meet and greets, coffee with a candidate, debates, forums, BBQs, ward meetings. Any public election event in Kawartha Lakes, for any race. Submissions are reviewed before they appear." />
      <form onSubmit={submit} noValidate className="space-y-6" data-testid="submit-event-form">
        <Card><CardContent className="p-5 sm:p-6 space-y-4">
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">1. The event</p>
          <div>
            <Label htmlFor="ev-title">Event title *</Label>
            <Input id="ev-title" value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={160} placeholder="e.g. Ward 3 All-Candidates Meeting" className={inputCls('title')} data-testid="submit-event-title" />
            <Err k="title" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ev-type">Event type *</Label>
              <Select value={form.event_type} onValueChange={(v) => set('event_type', v)}>
                <SelectTrigger id="ev-type" className="h-11 bg-white" data-testid="submit-event-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(types).length ? Object.entries(types).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>) : <SelectItem value="meet_greet">Meet and greet</SelectItem>}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ev-organizer">Organizer *</Label>
              <Input id="ev-organizer" value={form.organizer} onChange={(e) => set('organizer', e.target.value)} maxLength={160} placeholder="Group, association or campaign" className={inputCls('organizer')} data-testid="submit-event-organizer" />
              <Err k="organizer" />
            </div>
          </div>
          <div>
            <Label>Which race(s) is this event about? *</Label>
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="submit-event-races">
              {RACES.map((r) => (
                <label key={r.value} className={`flex items-center gap-2.5 rounded-lg border p-3 cursor-pointer ${form.races.includes(r.value) ? 'border-[#1a2744] bg-[#EAF2FF]' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                  <Checkbox checked={form.races.includes(r.value)} onCheckedChange={() => toggle('races', r.value)} data-testid={`submit-event-race-${r.value}`} />
                  <span className="text-sm font-semibold text-[#1a2744]">{r.label}</span>
                </label>
              ))}
            </div>
            <Err k="races" />
          </div>
          {form.races.includes('council') && (
            <div>
              <Label>Ward(s) *</Label>
              <div className="mt-1.5 flex flex-wrap gap-2" data-testid="submit-event-wards">
                {WARDS.map((w) => (
                  <button type="button" key={w} onClick={() => toggle('wards', w)} className={`h-10 min-w-[44px] px-3 rounded-lg border text-sm font-bold btn-press ${form.wards.includes(w) ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid={`submit-event-ward-${w}`} aria-pressed={form.wards.includes(w)}>Ward {w}</button>
                ))}
              </div>
              <Err k="wards" />
            </div>
          )}
          <div>
            <Label htmlFor="ev-candidates">Candidate(s) taking part (optional)</Label>
            <Input id="ev-candidates" value={form.candidates} onChange={(e) => set('candidates', e.target.value)} maxLength={600} placeholder="Names, separated by commas" className="h-11 bg-white" data-testid="submit-event-candidates" />
          </div>
          <div>
            <Label htmlFor="ev-description">Description *</Label>
            <Textarea id="ev-description" value={form.description} onChange={(e) => set('description', e.target.value)} rows={4} maxLength={3000} placeholder="What will happen, who should come, anything people need to know." className={`bg-white ${errors.description ? 'border-red-400' : ''}`} data-testid="submit-event-description" />
            <Err k="description" />
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-5 sm:p-6 space-y-4">
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">2. When and where</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="ev-date">Date *</Label>
              <Input id="ev-date" type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className={inputCls('date')} data-testid="submit-event-date" />
              <Err k="date" />
            </div>
            <div>
              <Label htmlFor="ev-start">Start time (ET)</Label>
              <Input id="ev-start" type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} className="h-11 bg-white" data-testid="submit-event-start" />
            </div>
            <div>
              <Label htmlFor="ev-end">End time (ET)</Label>
              <Input id="ev-end" type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} className="h-11 bg-white" data-testid="submit-event-end" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ev-venue">Venue name</Label>
              <Input id="ev-venue" value={form.venue_name} onChange={(e) => set('venue_name', e.target.value)} maxLength={200} placeholder="e.g. Omemee Community Hall" className={inputCls('venue_name')} data-testid="submit-event-venue" />
              <Err k="venue_name" />
            </div>
            <div>
              <Label htmlFor="ev-address">Address</Label>
              <Input id="ev-address" value={form.address} onChange={(e) => set('address', e.target.value)} maxLength={300} placeholder="Street, town" className="h-11 bg-white" data-testid="submit-event-address" />
            </div>
          </div>
          <div>
            <Label htmlFor="ev-online">Online event link (if any)</Label>
            <Input id="ev-online" value={form.online_url} onChange={(e) => set('online_url', e.target.value)} maxLength={500} placeholder="https://" className={inputCls('online_url')} data-testid="submit-event-online" />
            <Err k="online_url" />
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-5 sm:p-6 space-y-4">
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">3. Links and poster (optional)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ev-source">Source link (Facebook event, organizer page, news)</Label>
              <Input id="ev-source" value={form.source_url} onChange={(e) => set('source_url', e.target.value)} maxLength={500} placeholder="https://" className={inputCls('source_url')} data-testid="submit-event-source" />
              <Err k="source_url" />
            </div>
            <div>
              <Label htmlFor="ev-register">Registration link</Label>
              <Input id="ev-register" value={form.registration_url} onChange={(e) => set('registration_url', e.target.value)} maxLength={500} placeholder="https://" className={inputCls('registration_url')} data-testid="submit-event-registration" />
              <Err k="registration_url" />
            </div>
          </div>
          <div>
            <Label>Poster or image</Label>
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="submit-event-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-11 border-[#1a2744] text-[#1a2744] font-bold" data-testid="submit-event-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1.5" />} {form.image_url ? 'Replace image' : 'Upload image'}
              </Button>
              {form.image_url && (
                <>
                  <img src={fileUrl(form.image_url)} alt="Uploaded poster preview" className="h-16 w-24 object-cover rounded-md border border-slate-200" data-testid="submit-event-image-preview" />
                  <button type="button" onClick={() => set('image_url', '')} className="inline-flex items-center text-sm text-slate-600 underline" data-testid="submit-event-image-remove"><Trash2 className="h-3.5 w-3.5 mr-1" /> Remove</button>
                </>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">jpg, png, gif or webp, up to 8MB. Only upload an image you have the right to share.</p>
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-5 sm:p-6 space-y-4">
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">4. About you (kept private)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ev-name">Your name *</Label>
              <Input id="ev-name" value={form.submitter_name} onChange={(e) => set('submitter_name', e.target.value)} maxLength={120} autoComplete="name" className={inputCls('submitter_name')} data-testid="submit-event-submitter-name" />
              <Err k="submitter_name" />
            </div>
            <div>
              <Label htmlFor="ev-email">Your email *</Label>
              <Input id="ev-email" type="email" value={form.submitter_email} onChange={(e) => set('submitter_email', e.target.value)} maxLength={200} autoComplete="email" className={inputCls('submitter_email')} data-testid="submit-event-submitter-email" />
              <Err k="submitter_email" />
            </div>
          </div>
          {/* honeypot: hidden from people, filled by bots */}
          <div className="hidden" aria-hidden="true"><label htmlFor="ev-website">Website</label><input id="ev-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} /></div>
          <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${errors.consent ? 'border-red-400' : 'border-slate-200'}`}>
            <Checkbox checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-0.5" data-testid="submit-event-consent" />
            <span className="text-sm text-slate-700">I confirm this is a public event and that the details and any image may be published on this website after review. My name and email stay private and are used only to confirm details.</span>
          </label>
          <Err k="consent" />
          <p className="flex items-start gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 shrink-0 text-[#24406f]" /> Every submission is checked by a person before it appears. Events for all candidates and all wards are welcome. This is a resident resource, not an endorsement.</p>
          <Button type="submit" disabled={sending || uploading} className="h-12 px-8 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="submit-event-submit">
            {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending</> : 'Send for review'}
          </Button>
        </CardContent></Card>
      </form>
    </div>
  );
}
